import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Shield,
  Clock,
  Users,
  MapPin,
  Star,
  ChevronRight,
  Accessibility,
  CreditCard,
  ShoppingBag,
  Phone,
  Heart,
  Award
} from 'lucide-react';

const Home = () => {
  const features = [
    {
      icon: Accessibility,
      title: "Accessibilité Totale",
      description: "Parking et entrée accessibles aux fauteuils roulants"
    },
    {
      icon: ShoppingBag,
      title: "Retrait en Magasin",
      description: "Service de retrait rapide et pratique"
    },
    {
      icon: Clock,
      title: "Visite Rapide",
      description: "Service optimisé pour vos besoins urgents"
    },
    {
      icon: CreditCard,
      title: "Paiements Flexibles",
      description: "Cartes de crédit, débit et paiements NFC acceptés"
    }
  ];

  const stats = [
    { number: "6+", label: "Années d'expérience", icon: Award },
    { number: "100+", label: "Clients satisfaits", icon: Users },
    { number: "4.8/5", label: "Note moyenne", icon: Star },
    { number: "365j", label: "À votre service", icon: Heart }
  ];

  const testimonials = [
    {
      text: "Personnel très sympathique et professionnel ! Très bon !",
      author: "Jesse DeMaria-Kinney",
      rating: 5
    },
    {
      text: "Pharmacie propre nouvellement ouverte en face de l'hôpital.",
      author: "M BDV",
      rating: 5
    },
    {
      text: "Satisfaction totale, accueil chaleureux et professionnalisme de haut niveau.",
      author: "Rritë Kurti", 
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2523059669%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-medium">
                  <MapPin className="h-4 w-4 mr-2" />
                  Meyrin-Vergers • Face à l'hôpital
                </div>
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Votre <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">Pharmacie</span> de Confiance
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Découvrez une pharmacie moderne et accessible dans le quartier Vergers de Meyrin. 
                  Notre équipe professionnelle vous accueille avec expertise et bienveillance.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 text-lg">
                  <Phone className="h-5 w-5 mr-2" />
                  Nous Contacter
                </Button>
                <Link to="/services">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-4 text-lg w-full sm:w-auto"
                  >
                    Nos Services
                    <ChevronRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <stat.icon className="h-8 w-8 text-teal-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">{stat.number}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1698466632366-09fa1d925de6" 
                  alt="Pharmacie moderne et professionnelle"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              
              {/* Floating testimonial card */}
              <Card className="absolute -bottom-6 -left-6 bg-white shadow-xl border-0 max-w-sm">
                <CardContent className="p-6">
                  <div className="flex items-center mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-700 mb-3">
                    "Personnel très professionnel qui écoute et donne de bons conseils."
                  </p>
                  <p className="text-xs font-medium text-teal-600">Hassan Hassan</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Pourquoi Choisir Notre Pharmacie ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une expérience moderne, accessible et personnalisée au cœur de Meyrin-Vergers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8 text-center">
                  <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ce Que Disent Nos Clients
            </h2>
            <p className="text-xl text-gray-600">
              Des avis authentiques de notre communauté de Meyrin-Vergers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">
                    "{testimonial.text}"
                  </p>
                  <div className="font-medium text-teal-600">
                    {testimonial.author}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/reviews">
              <Button variant="outline" size="lg" className="border-teal-600 text-teal-600 hover:bg-teal-50">
                Voir Tous les Avis
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-6">
              Prêt à Découvrir Notre Service ?
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              Contactez-nous dès aujourd'hui ou rendez-vous dans notre pharmacie moderne 
              du quartier Vergers de Meyrin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="bg-white text-teal-600 hover:bg-gray-50 px-8 py-4">
                  <MapPin className="h-5 w-5 mr-2" />
                  Nous Trouver
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4">
                <Phone className="h-5 w-5 mr-2" />
                +41 22 987 59 17
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;